import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { X } from 'lucide-react';
import { safeUrl, openInWorkingTab } from '../utils/navigate';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BannerData {
  id: string;
  html: string;
  imageUrl?: string;
  link?: string;
  position: 'top' | 'inline' | 'bottom';
  campaignId: string;
}

interface MarketingBannerProps {
  /** Banner data to render */
  banner: BannerData;
  /** Called when the banner is dismissed */
  onDismiss?: (bannerId: string) => void;
  /** Called when the banner registers an impression */
  onImpression?: (bannerId: string, campaignId: string) => void;
  /** Called when the banner is clicked */
  onClick?: (bannerId: string, campaignId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Marketing banner component for the search widget.
 * Supports image banners and HTML banners at top, inline, or bottom positions.
 * Features dismiss button, smooth entrance animation, and impression tracking.
 */
export const MarketingBanner: React.FC<MarketingBannerProps> = ({
  banner,
  onDismiss,
  onImpression,
  onClick,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(true);
  const impressionTracked = useRef(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Track impression when banner becomes visible
  useEffect(() => {
    if (!impressionTracked.current && isVisible && onImpression) {
      impressionTracked.current = true;

      // Use IntersectionObserver for actual viewport visibility
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              onImpression(banner.id, banner.campaignId);
              observer.disconnect();
            }
          }
        },
        { threshold: 0.5 }
      );

      if (bannerRef.current) {
        observer.observe(bannerRef.current);
      }

      return () => observer.disconnect();
    }
  }, [isVisible, banner.id, banner.campaignId, onImpression]);

  // Entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setIsAnimating(false), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsVisible(false);
      onDismiss?.(banner.id);
    },
    [banner.id, onDismiss]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      onClick?.(banner.id, banner.campaignId);
      if (banner.link) openInWorkingTab(banner.link, e);
    },
    [banner.id, banner.campaignId, banner.link, onClick],
  );

  if (!isVisible) return null;

  const positionClasses: Record<string, string> = {
    top: 'mb-3',
    inline: 'my-3',
    bottom: 'mt-3',
  };

  const Wrapper = banner.link ? 'a' : 'div';
  const wrapperProps = banner.link
    ? {
        href: safeUrl(banner.link),
        onClick: handleClick,
      }
    : {};

  return (
    <div
      ref={bannerRef}
      className={[
        'relative rounded-xl overflow-hidden',
        positionClasses[banner.position] ?? 'mb-3',
        isAnimating
          ? 'opacity-0 translate-y-2'
          : 'opacity-100 translate-y-0',
        'transition-all duration-300 ease-out',
      ].join(' ')}
      role="banner"
      aria-label="Προωθητική ανακοίνωση"
    >
      <Wrapper
        {...wrapperProps}
        className={[
          'block w-full',
          banner.link ? 'cursor-pointer hover:opacity-95 transition-opacity' : '',
        ].join(' ')}
      >
        {/* Image banner */}
        {banner.imageUrl && (
          <img
            src={banner.imageUrl}
            alt="Προσφορά"
            className="w-full h-auto rounded-xl object-cover max-h-[200px]"
            loading="lazy"
          />
        )}

        {/* HTML banner */}
        {banner.html && !banner.imageUrl && (
          <div
            className={[
              'px-4 py-3 rounded-xl',
              'bg-gradient-to-r from-wine/10 via-wine/5 to-transparent',
              'border border-wine/15',
              'text-sm text-gray-800',
            ].join(' ')}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(banner.html) }}
          />
        )}
      </Wrapper>

      {/* Dismiss button */}
      <button
        type="button"
        onClick={handleDismiss}
        className={[
          'absolute top-2 right-2 z-10',
          'w-6 h-6 rounded-full',
          'bg-black/40 hover:bg-black/60',
          'flex items-center justify-center',
          'text-white transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-white/50',
        ].join(' ')}
        aria-label="Κλείσιμο banner"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Banner list component (renders multiple banners by position)
// ---------------------------------------------------------------------------

interface MarketingBannerListProps {
  banners: BannerData[];
  position: 'top' | 'inline' | 'bottom';
  onDismiss?: (bannerId: string) => void;
  onImpression?: (bannerId: string, campaignId: string) => void;
  onClick?: (bannerId: string, campaignId: string) => void;
}

/**
 * Renders a filtered list of banners for a specific position (top/inline/bottom).
 */
export const MarketingBannerList: React.FC<MarketingBannerListProps> = ({
  banners,
  position,
  onDismiss,
  onImpression,
  onClick,
}) => {
  const filteredBanners = banners.filter((b) => b.position === position);

  if (filteredBanners.length === 0) return null;

  return (
    <div className="botilia-marketing-banners">
      {filteredBanners.map((banner) => (
        <MarketingBanner
          key={banner.id}
          banner={banner}
          onDismiss={onDismiss}
          onImpression={onImpression}
          onClick={onClick}
        />
      ))}
    </div>
  );
};
